/**
 * Importing one image on the page (decision 56; part 16a log, table I): the file's size checked before it is read, an
 * image worker started for it alone and ended after its reply or after {@link IMAGE_TIME_MS}, and what the worker made
 * handed to the image library, which checks it again and stores it (table A). The worker, the timer and the library's
 * add are injected, so the logic runs in tests.
 */
import { MAX_IMAGE_BYTES } from '@cockatrice/core';
import type { ImagePurpose, ImageReference, ImageRefusal } from '@cockatrice/core';
import type { EncodedImage, ImageAddOutcome } from '@cockatrice/storage';
import { readReply } from './protocol.ts';
import type { CompareJob, ImageJob, ImageReply } from './protocol.ts';

/** I4: how long the worker may take over one image, in milliseconds, before it is ended. */
export const IMAGE_TIME_MS = 30_000;

/** An image worker, as far as the importer drives it (a `Worker`). */
export interface ImageWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: ImageJob | CompareJob, options: { readonly transfer: Transferable[] }): void;
  terminate(): void;
}

/**
 * What importing one file gives: the reference of the image stored or found in the book (I6), why the file isn't an image
 * the app takes (I1, I5), no room in the book (I6), or a failure anywhere else (I3, I4, I7).
 */
export type ImportResult =
  | { readonly kind: 'added' | 'existing'; readonly reference: ImageReference }
  | { readonly kind: 'refused'; readonly reason: ImageRefusal }
  | { readonly kind: 'full' }
  | { readonly kind: 'failed' };

/** What the importer needs; everything with an effect is injected. */
export interface ImageImportOptions {
  /** Starts an image worker. */
  readonly worker: () => ImageWorker;
  /** The image library's add (`ImageLibrary.add`). */
  readonly add: (bookId: string, image: EncodedImage) => Promise<ImageAddOutcome>;
  /** `setTimeout` and `clearTimeout`. */
  readonly setTimer: (run: () => void, ms: number) => unknown;
  readonly clearTimer: (timer: unknown) => void;
}

const failed: ImportResult = { kind: 'failed' };

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/**
 * I2–I4: the reply a worker started for `job` sends, or null when it can't start, errors, sends something unreadable or
 * takes too long; the worker is ended at the first of these, and whatever comes after is ignored.
 */
function askWorker(job: ImageJob | CompareJob, options: Omit<ImageImportOptions, 'add'>): Promise<ImageReply | null> {
  return new Promise((resolve) => {
    let worker: ImageWorker;
    try {
      worker = options.worker();
    } catch {
      resolve(null);
      return;
    }
    let ended = false;
    let timer: unknown = undefined;
    const end = (reply: ImageReply | null): void => {
      if (ended) return;
      ended = true;
      options.clearTimer(timer);
      worker.terminate();
      resolve(reply);
    };
    worker.addEventListener('message', (event) => {
      end(readReply(dataOf(event)));
    });
    worker.addEventListener('error', () => {
      end(null);
    });
    worker.addEventListener('messageerror', () => {
      end(null);
    });
    timer = options.setTimer(() => {
      end(null);
    }, IMAGE_TIME_MS);
    try {
      worker.postMessage(job, { transfer: job.kind === 'compare' ? [job.bytes, job.stored] : [job.bytes] });
    } catch {
      end(null);
    }
  });
}

/** Table I: `file`, meant for `purpose`, made into an image of book `bookId` and stored, or why not. */
export async function importImage(bookId: string, file: Blob, purpose: ImagePurpose, options: ImageImportOptions): Promise<ImportResult> {
  // I1: never read.
  if (file.size > MAX_IMAGE_BYTES) return { kind: 'refused', reason: 'too-large' };
  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch {
    return failed;
  }
  const reply = await askWorker({ kind: 'encode', bytes, purpose }, options);
  if (reply === null || reply.kind === 'compared') return failed;
  if (reply.kind === 'refused') return reply.reason === 'failed' ? failed : { kind: 'refused', reason: reply.reason };
  let outcome: ImageAddOutcome;
  try {
    outcome = await options.add(bookId, { bytes: new Uint8Array(reply.bytes), format: reply.format, width: reply.width, height: reply.height });
  } catch {
    return failed;
  }
  if (outcome.kind === 'added' || outcome.kind === 'existing') return { kind: outcome.kind, reference: outcome.reference };
  return outcome.kind === 'full' ? { kind: 'full' } : failed;
}

/**
 * Part 23b, table PX: whether the picture `file` (a re-imported one) shows what `stored` does, asked of an image worker
 * started for it alone and ended as an import's is (I2–I4); false for a file past the image cap and for any failure on
 * the way, so the picture goes on as a new one (PI8).
 */
export async function comparePicture(file: Blob, stored: Uint8Array, options: Omit<ImageImportOptions, 'add'>): Promise<boolean> {
  if (file.size > MAX_IMAGE_BYTES) return false;
  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch {
    return false;
  }
  const reply = await askWorker({ kind: 'compare', bytes, stored: Uint8Array.from(stored).buffer }, options);
  return reply?.kind === 'compared' && reply.same;
}
